import { Controller } from "@hotwired/stimulus"

const SCRIPT_ID = "cf-turnstile-api"
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

// Gate form submit until Cloudflare Turnstile has issued a token.
// Explicit render + polling so Turbo Drive navigations (e.g. Sign Up nav link) still show the widget.
export default class extends Controller {
  static values = {
    sitekey: String
  }

  static targets = ["widget", "submit"]

  connect() {
    this.token = null
    this.widgetId = null
    this.setSubmitEnabled(false)
    this.boundSubmit = this.preventUncheckedSubmit.bind(this)
    this.form?.addEventListener("submit", this.boundSubmit)

    // Defer one frame so Turbo has finished swapping the body before we render.
    this.renderFrame = requestAnimationFrame(() => this.loadAndRender())
  }

  disconnect() {
    if (this.renderFrame) cancelAnimationFrame(this.renderFrame)
    if (this.readyPoll) clearInterval(this.readyPoll)

    this.form?.removeEventListener("submit", this.boundSubmit)
    if (this.widgetId != null && window.turnstile) {
      try {
        window.turnstile.remove(this.widgetId)
      } catch (_) {
        // Widget may already be gone after Turbo replaces the form.
      }
    }
    this.widgetId = null
  }

  get form() {
    return this.element.matches("form") ? this.element : this.element.closest("form")
  }

  get submitButton() {
    if (this.hasSubmitTarget) return this.submitTarget
    return this.form?.querySelector('input[type="submit"], button[type="submit"]')
  }

  setSubmitEnabled(enabled) {
    const button = this.submitButton
    if (!button) return

    button.disabled = !enabled
    button.setAttribute("aria-disabled", enabled ? "false" : "true")
  }

  preventUncheckedSubmit(event) {
    if (this.token) return

    event.preventDefault()
    event.stopImmediatePropagation()
    this.setSubmitEnabled(false)
  }

  onSuccess(token) {
    this.token = token
    this.setSubmitEnabled(true)
  }

  onExpire() {
    this.token = null
    this.setSubmitEnabled(false)
  }

  onError() {
    this.token = null
    this.setSubmitEnabled(false)
  }

  loadAndRender() {
    this.whenTurnstileReady()
      .then(() => this.renderWidget())
      .catch((error) => {
        console.error("[Turnstile]", error)
        this.setSubmitEnabled(false)
      })
  }

  whenTurnstileReady() {
    if (window.turnstile?.render) return Promise.resolve()

    this.ensureScript()

    return new Promise((resolve, reject) => {
      const startedAt = Date.now()

      const finish = () => {
        if (this.readyPoll) {
          clearInterval(this.readyPoll)
          this.readyPoll = null
        }
        resolve()
      }

      // Poll covers: cached script with no second "load" event, and Turbo revisits.
      this.readyPoll = setInterval(() => {
        if (window.turnstile?.render) {
          finish()
        } else if (Date.now() - startedAt > 10000) {
          clearInterval(this.readyPoll)
          this.readyPoll = null
          reject(new Error("Timed out waiting for Cloudflare Turnstile"))
        }
      }, 50)
    })
  }

  ensureScript() {
    if (document.getElementById(SCRIPT_ID)) return

    const script = document.createElement("script")
    script.id = SCRIPT_ID
    script.src = SCRIPT_SRC
    script.async = true
    // Attach onload before inserting to avoid missing a cached/sync load event.
    script.onload = () => {}
    document.head.appendChild(script)
  }

  renderWidget() {
    if (!this.hasWidgetTarget || !window.turnstile?.render) return
    if (!this.sitekeyValue) return
    // Already rendered for this connect cycle.
    if (this.widgetId != null) return

    this.widgetTarget.innerHTML = ""
    this.widgetId = window.turnstile.render(this.widgetTarget, {
      sitekey: this.sitekeyValue,
      theme: "light",
      callback: (token) => this.onSuccess(token),
      "expired-callback": () => this.onExpire(),
      "error-callback": () => this.onError(),
      "timeout-callback": () => this.onExpire()
    })
  }
}
